import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import { useSettings } from "@ext/enterprise/components/admin/contexts/SettingsContext";
import { useAccessSnapshot } from "@ext/enterprise/components/admin/settings/members/hooks/useAccessSnapshot";
import { FloatingAlert } from "@ext/enterprise/components/admin/ui-kit/FloatingAlert";
import { TableToolbarTextInput } from "@ext/enterprise/components/admin/ui-kit/table/TableToolbarTextInput";
import { GesCloudWsSettingsProvider } from "@ext/enterprise-cloud/components/organizationSettings/contexts/GesCloudWsSettingsProvider";
import {
	type BillingModeCode,
	type BillingPeriodCode,
	GesCloudApi,
	type GesCloudSubscription,
} from "@ext/enterprise-cloud/GesCloudApi";
import t from "@ext/localization/locale/translate";
import { Button } from "@ui-kit/Button";
import { getCoreRowModel, getFilteredRowModel, type Row, useReactTable } from "@ui-kit/DataTable";
import { Skeleton } from "@ui-kit/Skeleton";
import { type ComponentProps, useCallback, useEffect, useMemo, useState } from "react";
import ModalToOpenService from "../../../../../../ui-logic/ContextServices/ModalToOpenService/ModalToOpenService";
import ModalToOpen from "../../../../../../ui-logic/ContextServices/ModalToOpenService/model/ModalsToOpen";
import { showPopover } from "../../../../../../ui-logic/showPopover";
import { useOpenState } from "../../../../../enterprise/components/admin/hooks/useOpenState";
import { useSheetSlot } from "../../../../../enterprise/components/admin/hooks/useSheetSlot";
import { DeleteSelectedConfirm } from "../../../../../enterprise/components/admin/ui-kit/DeleteSelectedConfirm";
import { sectionTitleClass } from "../../../../../enterprise/components/admin/ui-kit/SettingsSection";
import { TableComponent } from "../../../../../enterprise/components/admin/ui-kit/table/TableComponent";
import { TableToolbar } from "../../../../../enterprise/components/admin/ui-kit/table/TableToolbar";
import type { PurchaseSeatPayModal } from "../../../payModals/PurchaseSeatPayModal";
import { SubscriptionInactiveAlert } from "../../../SubscriptionInactiveAlert/SubscriptionInactiveAlert";
import { RefreshDataButton } from "../../components/RefreshDataButton";
import { EditMemberCard } from "../components/EditMemberCard";
import { InviteMemberCard } from "../components/InviteMemberCard";
import type { PayerTypeChooseModal } from "../components/PayerTypeChooseModal";
import type { TariffChooseModal } from "../components/TariffChooseModal";
import { getGesCloudUsersTableColumns } from "./config/GesCloudUsersTableConfig";
import { isOrganizationOwner } from "./isOrganizationOwner";
import type {
	AccessChange,
	GesCloudMember,
	InviteMemberPayload,
	MemberTarget,
	UserAccessChange,
} from "./types/GesCloudUsersComponentTypes";

export type GesCloudUsersComponentProps = {
	subscription: GesCloudSubscription | null;
	refreshSubscription: () => void;
};

const GesCloudUsersContent = ({ subscription, refreshSubscription }: GesCloudUsersComponentProps) => {
	const { url: gesCloudUrl } = PageDataContextService.value.conf.enterpriseCloud;
	const gesCloudApi = useMemo(() => new GesCloudApi(gesCloudUrl), [gesCloudUrl]);
	const { ensureLoaded } = useSettings();
	const { aggregate, getChangedResources } = useAccessSnapshot({ enabled: true });

	const [members, setMembers] = useState<GesCloudMember[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [rowSelection, setRowSelection] = useState({});
	const [saveError, setSaveError] = useState<string | null>(null);
	const singleSlot = useSheetSlot<GesCloudMember | null>({ keyBase: "single" });
	const inviteSheet = useOpenState({ keyBase: "invite" });

	const currentUserMail = PageDataContextService.value.user.info?.mail;
	const isCurrentUserOwner = useMemo(() => isOrganizationOwner(members, currentUserMail), [members, currentUserMail]);
	const isCurrentUserAdmin = useMemo(
		() => members.some((member) => member.type === "user" && member.isAdmin && member.email === currentUserMail),
		[members, currentUserMail],
	);
	const canEditMembers = isCurrentUserOwner || isCurrentUserAdmin;

	const refreshMembers = useCallback(async () => {
		const membersData = await gesCloudApi.getMembers();
		setMembers(membersData);
	}, [gesCloudApi]);

	const refreshData = useCallback(async () => {
		try {
			setIsLoading(true);
			const [membersData] = await Promise.all([gesCloudApi.getMembers(), refreshSubscription()]);
			setMembers(membersData);
		} catch (error) {
			setSaveError(error instanceof Error ? error.message : "Failed to load members or subscription");
		} finally {
			setIsLoading(false);
		}
	}, [gesCloudApi, refreshSubscription]);

	useEffect(() => {
		void refreshData();
	}, [refreshData]);

	const maxSeats = subscription?.seatsInfo.maxSeats ?? 0;

	const columns = useMemo(
		() => getGesCloudUsersTableColumns({ isCurrentUserOwner, canEditMembers, aggregate }),
		[isCurrentUserOwner, canEditMembers, aggregate],
	);

	const table = useReactTable({
		data: members,
		columns,
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		onRowSelectionChange: setRowSelection,
		state: {
			rowSelection,
		},
		defaultColumn: { size: 0 },
	});

	// biome-ignore lint/correctness/useExhaustiveDependencies: delete button wont appear without this
	const selectedCount = useMemo(() => table.getFilteredSelectedRowModel().rows.length, [table, rowSelection]);

	const deleteSelected = useCallback(async () => {
		const selectedRows = table.getFilteredSelectedRowModel().rows;
		if (selectedRows.some((row) => row.original.isAdmin) && isCurrentUserAdmin) return;
		const selectedUserEmails = selectedRows.map((row) => row.original.email);

		try {
			await gesCloudApi.excludeMembers(selectedUserEmails);
			const updatedMembers = members.filter((member) => !selectedUserEmails.includes(member.email));
			setMembers(updatedMembers);
			setRowSelection({});
			await ensureLoaded("editors", true);
		} catch (error) {
			setSaveError(error instanceof Error ? error.message : "Failed to exclude members");
		}
	}, [members, table, gesCloudApi, isCurrentUserAdmin, ensureLoaded]);

	const handleInviteMember = useCallback(
		async ({ accessChanges, ...invite }: InviteMemberPayload) => {
			const resources = getChangedResources(accessChanges);
			await gesCloudApi.inviteMember({ ...invite, resources });

			const membersRefresh = refreshMembers();
			const ensureEditorsLoaded = invite.workspaceRole === "editor" ? ensureLoaded("editors", true) : undefined;
			const ensureResourcesLoaded = resources.length > 0 ? ensureLoaded("resources", true) : undefined;
			await Promise.all([membersRefresh, ensureEditorsLoaded, ensureResourcesLoaded]);

			showPopover(t("enterprise-cloud.org-settings.users.invite-toast"));
			setSaveError(null);
		},
		[gesCloudApi, getChangedResources, refreshMembers, ensureLoaded],
	);

	const handleFilterChange = useCallback(
		(value: string | null) => {
			table.getColumn("email")?.setFilterValue(value);
		},
		[table],
	);

	const applyMemberChanges = useCallback(
		async (target: MemberTarget, changes: AccessChange[]) => {
			let rolesChange: Extract<AccessChange, { kind: "setMemberRoles" }> | undefined;
			let aiAgentAccessChange: Extract<AccessChange, { kind: "setAiAgentAccess" }> | undefined;
			const accessChanges: UserAccessChange[] = [];

			for (const change of changes) {
				if (change.kind === "setMemberRoles") rolesChange = change;
				else if (change.kind === "setAiAgentAccess") aiAgentAccessChange = change;
				else accessChanges.push(change);
			}

			const changedResources = getChangedResources(accessChanges);
			await gesCloudApi.updateMember({
				target,
				role: rolesChange?.role,
				setAdmin: rolesChange?.setAdmin,
				aiAgentAccess: aiAgentAccessChange?.aiAgentAccess,
				resources: changedResources,
			});

			const ensureEditorsLoaded = ensureLoaded("editors", true);
			const membersRefresh = refreshMembers();
			const ensureResourcesLoaded = changedResources.length > 0 ? ensureLoaded("resources", true) : undefined;

			await Promise.all([ensureEditorsLoaded, membersRefresh, ensureResourcesLoaded]);
		},
		[gesCloudApi, getChangedResources, ensureLoaded, refreshMembers],
	);

	const existingUsers = useMemo(() => members.map((member) => member.email), [members]);

	const openPurchaseSeatPayModal = useCallback(
		(
			minAddedSeatsCount: number,
			currentSeatsCount: number,
			billingPeriod?: BillingPeriodCode,
			billingMode: BillingModeCode = subscription?.billingMode ?? "individual",
		) => {
			if (!subscription) return;

			const paymentModalId = ModalToOpenService.addModal<ComponentProps<typeof PurchaseSeatPayModal>>(
				ModalToOpen.GesCloudPurchaseSeatPayModal,
				{
					minAddedSeatsCount,
					currentSeatsCount,
					billingPeriod,
					billingMode,
					subscription,
					onSuccess: refreshSubscription,
					onClose: () => ModalToOpenService.removeModal(paymentModalId),
				},
			);
		},
		[refreshSubscription, subscription],
	);

	const openPayerTypeChooseModal = useCallback(
		(minAddedSeatsCount: number, currentSeatsCount: number, billingPeriod?: BillingPeriodCode) => {
			const payerTypeModalId = ModalToOpenService.addModal<ComponentProps<typeof PayerTypeChooseModal>>(
				ModalToOpen.GesCloudPayerTypeChoose,
				{
					onIndividualContinue: () =>
						openPurchaseSeatPayModal(minAddedSeatsCount, currentSeatsCount, billingPeriod, "individual"),
					onLegalEntitySuccess: async () => {
						await refreshSubscription();
						openPurchaseSeatPayModal(minAddedSeatsCount, currentSeatsCount, billingPeriod, "legal_entity");
					},
					onClose: () => ModalToOpenService.removeModal(payerTypeModalId),
				},
			);
		},
		[openPurchaseSeatPayModal, refreshSubscription],
	);

	const openPaymentFlow = useCallback(
		async (minAddedSeatsCount: number, currentSeatsCount: number, billingPeriod?: BillingPeriodCode) => {
			try {
				const paymentMethods = await gesCloudApi.getPaymentMethods();
				if (paymentMethods.length > 0) {
					openPurchaseSeatPayModal(minAddedSeatsCount, currentSeatsCount, billingPeriod);
					return;
				}

				openPayerTypeChooseModal(minAddedSeatsCount, currentSeatsCount, billingPeriod);
			} catch {
				setSaveError(t("enterprise-cloud.org-settings.subscription.payment.payment-methods-load-error"));
			}
		},
		[gesCloudApi, openPurchaseSeatPayModal, openPayerTypeChooseModal],
	);

	const handleBuySeatsClick = useCallback(() => {
		if (subscription?.plan === "paid") {
			openPurchaseSeatPayModal(1, maxSeats);
			return;
		}

		const subscriptionModalId = ModalToOpenService.addModal<ComponentProps<typeof TariffChooseModal>>(
			ModalToOpen.TariffChoose,
			{
				description: t("enterprise-cloud.org-settings.subscription.required-description"),
				onContinue: (billingPeriod) => void openPaymentFlow(1, maxSeats, billingPeriod),
				onClose: () => ModalToOpenService.removeModal(subscriptionModalId),
			},
		);
	}, [maxSeats, subscription, openPurchaseSeatPayModal, openPaymentFlow]);

	return (
		<>
			{subscription && (
				<SubscriptionInactiveAlert onRecovered={refreshSubscription} subscriptionInfo={subscription} />
			)}
			<FloatingAlert message={saveError} show={Boolean(saveError)} />

			<div className="flex items-baseline gap-2">
				<h2 className={`${sectionTitleClass} flex items-center gap-1 whitespace-nowrap`}>
					<span>{t("enterprise-cloud.org-settings.pages.users")}</span>
					<CounterValue isLoading={isLoading} value={members.length} />
				</h2>

				<span className="text-muted">/</span>

				<h2 className={`${sectionTitleClass} flex items-center gap-1 whitespace-nowrap`}>
					<span>{t("enterprise-cloud.org-settings.users.editor-seats")}</span>
					<CounterValue isLoading={isLoading} value={`${aggregate.editors.length}/${maxSeats}`} wide />
				</h2>
			</div>

			<div>
				<TableToolbar
					input={
						<TableToolbarTextInput
							onChange={handleFilterChange}
							placeholder={t("enterprise-cloud.org-settings.users.placeholder")}
							value={(table.getColumn("email")?.getFilterValue() as string) ?? ""}
						/>
					}
				>
					<DeleteSelectedConfirm onConfirm={deleteSelected} selectedCount={selectedCount} />

					<RefreshDataButton handleRefresh={refreshData} />

					<Button className="ml-auto" onClick={handleBuySeatsClick} startIcon="credit-card" variant="outline">
						{t("enterprise-cloud.org-settings.users.buy-seats")}
					</Button>

					<Button onClick={inviteSheet.open} startIcon="user-plus" variant="outline">
						{t("enterprise-cloud.org-settings.users.invite")}
					</Button>
				</TableToolbar>

				<TableComponent<GesCloudMember>
					columns={columns}
					isLoading={isLoading}
					onRowClick={(row: Row<GesCloudMember>) => singleSlot.openWith(row.original)}
					table={table}
				/>

				{canEditMembers && (
					<EditMemberCard
						aggregate={aggregate}
						canManageAdmins={isCurrentUserOwner}
						key={singleSlot.session}
						onApply={applyMemberChanges}
						onClose={singleSlot.close}
						open={singleSlot.isOpen}
						user={singleSlot.data}
					/>
				)}

				<InviteMemberCard
					aggregate={aggregate}
					canManageAdmins={isCurrentUserOwner}
					existingUsers={existingUsers}
					key={inviteSheet.key}
					onClose={inviteSheet.close}
					onInvite={handleInviteMember}
					open={inviteSheet.isOpen}
					showAccesses
				/>
			</div>
		</>
	);
};

const GesCloudUsersComponent = (props: GesCloudUsersComponentProps) => (
	<GesCloudWsSettingsProvider>
		<GesCloudUsersContent {...props} />
	</GesCloudWsSettingsProvider>
);

interface CounterValueProps {
	isLoading: boolean;
	value: string | number;
	wide?: boolean;
}

const CounterValue = ({ isLoading, value, wide }: CounterValueProps) => {
	return (
		<div className={`h-6 ${wide ? "min-w-10" : "min-w-4"} flex items-center`}>
			{isLoading ? (
				<Skeleton className="h-6 w-full" />
			) : (
				<span className="tabular-nums w-full text-center">{value}</span>
			)}
		</div>
	);
};

export default GesCloudUsersComponent;
